import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/ui/StatCard';
import { PageContainer } from '@/components/ui/PageContainer';
import { Select } from '@/components/ui/Select/Select';
import { Input } from '@/components/ui/Input';
import { SimpleTooltip } from '@/components/ui/Tooltip/Tooltip';
import { FileViewerModal, type FileViewerFile } from '@/components/ui/FileViewer/FileViewerModal';
import { AirportAutocomplete } from '@/components/ui/Autocomplete/AirportAutocomplete';
import {
	FlightManifestService,
	type EventManifestDataDTO,
	type RecordWithSourceDTO,
} from '@/services/FlightManifestService';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { apiFetch } from '@/lib/api';
import { useAirportNames } from '@/hooks/useAirportNames';
import { CreateManifestWizard } from './components/CreateManifestWizard';
import { ManifestAgentPanel } from './agent/ManifestAgentPanel';
import { AttendeeManagementModal } from './components/AttendeeManagementModal';
import { useNotificationStore } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog';
import {
	ArrowLeft, Plus, CheckCircle, FileText, User, Users, Eye, Search,
	AlertTriangle, Download, ChevronUp, ChevronDown, Pencil, X, Check,
	RefreshCw, Trash2, List, Settings2, Bot,
} from 'lucide-react';
import {
	type FlightLeg, type BookingGroup, type PassengerGroup, type EditingCell, type NewLegData,
	ANOMALY_TYPE_ICONS, ANOMALY_TYPE_LABELS, ANOMALY_TYPE_TINTS,
	parseDate, formatDate, formatTime24, getRecordAnomalyTypes, buildLegsFromRecord, groupRecordsByPassenger,
	EditableTextCell, EditableNameCell, EditableDateCell, EditableTimeCell, EditablePNRCell, EditableAirportRoute, SourceFileButton, PassengerGroupRows,
} from './components/shared/ManifestTableComponents';

type SortKey = 'passenger_name' | 'primary_pnr' | 'outbound_departure_date';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all';
type ViewMode = 'simple' | 'advanced';

// ═══════════════════════════════════════════════════════════════════
// Page-specific Helpers
// ═══════════════════════════════════════════════════════════════════

function passengerMatchesSearch(group: PassengerGroup, q: string): boolean {
	const lower = q.toLowerCase();
	if (group.name.toLowerCase().includes(lower)) return true;
	if (group.email?.toLowerCase().includes(lower)) return true;
	for (const b of group.bookings) {
		if (b.pnr.toLowerCase().includes(lower)) return true;
		if (b.sourceFilename.toLowerCase().includes(lower)) return true;
		for (const leg of b.legs) {
			if (leg.departureAirport?.toLowerCase().includes(lower)) return true;
			if (leg.arrivalAirport?.toLowerCase().includes(lower)) return true;
			if (leg.flightNumber?.toLowerCase().includes(lower)) return true;
		}
	}
	return false;
}

function getPassengerSortValue(group: PassengerGroup, key: SortKey): string {
	switch (key) {
		case 'passenger_name': return group.name.toLowerCase();
		case 'primary_pnr': return (group.bookings[0]?.pnr || '').toLowerCase();
		case 'outbound_departure_date': return group.bookings[0]?.legs[0]?.date || '';
		default: return '';
	}
}

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;

function getDateRange(records: RecordWithSourceDTO[]): string {
	const dates = records.map(r => r.outbound_departure_date).filter(Boolean).sort();
	if (dates.length === 0) return '\u2014';
	const firstDt = parseDate(dates[0]!);
	const lastDt = parseDate(dates[dates.length - 1]!);
	if (isNaN(firstDt.getTime())) return '\u2014';
	const fmtCompact = (dt: Date) => `${String(dt.getDate()).padStart(2, '0')}/${MONTH_ABBR[dt.getMonth()]}`;
	const first = fmtCompact(firstDt);
	const last = isNaN(lastDt.getTime()) ? first : fmtCompact(lastDt);
	return first === last ? first : `${first} \u2014 ${last}`;
}

/** Extract condensed first-leg / last-leg summary for simple view */
function getRecordFlightSummary(r: RecordWithSourceDTO) {
	const legs = buildLegsFromRecord(r);
	// Sort by date + time
	const sorted = [...legs].sort((a, b) => {
		const da = `${a.date || ''}T${a.departureTime || '00:00'}`;
		const db = `${b.date || ''}T${b.departureTime || '00:00'}`;
		return da.localeCompare(db);
	});
	const first = sorted[0] || null;
	const last = sorted.length > 1 ? sorted[sorted.length - 1] : first;
	return {
		route: first && last
			? `${first.departureAirport || '?'} → ${last.arrivalAirport || '?'}`
			: '—',
		firstDepartureDate: first?.date || null,
		firstDepartureTime: first?.departureTime || null,
		lastLegDepartureDate: last?.date || null,
		lastLegDepartureTime: last?.departureTime || null,
		lastArrivalDate: last?.date || null,
		lastArrivalTime: last?.arrivalTime || null,
		legCount: sorted.length,
	};
}

const formatDateCompact = (d: string | null | undefined) => {
	if (!d) return '—';
	const dt = parseDate(d);
	if (isNaN(dt.getTime())) return '—';
	const dd = String(dt.getDate()).padStart(2, '0');
	return `${dd}/${MONTH_ABBR[dt.getMonth()]}`;
};

/** Check if a time string (HH:MM) is within ±windowMin of target (HH:MM) */
function timeInWindow(time: string | null | undefined, target: string, windowMin: number): boolean {
	if (!time || !target) return false;
	const [th, tm] = target.split(':').map(Number);
	const [rh, rm] = time.slice(0, 5).split(':').map(Number);
	if (isNaN(th) || isNaN(tm) || isNaN(rh) || isNaN(rm)) return false;
	const targetMins = th * 60 + tm;
	const recordMins = rh * 60 + rm;
	const diff = Math.abs(targetMins - recordMins);
	// Handle wrap-around midnight
	return Math.min(diff, 1440 - diff) <= windowMin;
}

/** Check if a record's date fields match a given date (YYYY-MM-DD), searching outbound dep or return dep */
/** Normalize an ISO date or YYYY-MM-DD to just YYYY-MM-DD */
const normDate = (d: string | null | undefined) => d ? d.slice(0, 10) : '';

function recordMatchesSearch(r: RecordWithSourceDTO, q: string): boolean {
	const lower = q.toLowerCase();
	if (r.passenger_name?.toLowerCase().includes(lower)) return true;
	if (r.passenger_email?.toLowerCase().includes(lower)) return true;
	if (r.primary_pnr?.toLowerCase().includes(lower)) return true;
	if (r.source_filename?.toLowerCase().includes(lower)) return true;
	if (r.outbound_departure_airport?.toLowerCase().includes(lower)) return true;
	if (r.outbound_arrival_airport?.toLowerCase().includes(lower)) return true;
	if (r.outbound_flight_number?.toLowerCase().includes(lower)) return true;
	if (r.return_departure_airport?.toLowerCase().includes(lower)) return true;
	if (r.return_arrival_airport?.toLowerCase().includes(lower)) return true;
	return false;
}

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

export function EventManifests() {
	const { eventId } = useParams<{ eventId: string }>();
	const navigate = useNavigate();
	const manifestService = useMemo(() => new FlightManifestService(), []);
	const eventsService = useMemo(() => new EventsService(), []);
	const notifyError = useNotificationStore(s => s.error);
	const notifySuccess = useNotificationStore(s => s.success);

	const [loading, setLoading] = useState(true);
	const [event, setEvent] = useState<EventDTO | null>(null);
	const [data, setData] = useState<EventManifestDataDTO | null>(null);
	const [wizardOpen, setWizardOpen] = useState(false);
	// ── LAUNCH SWITCH (operator, 2026-09-15; env-driven 2026-09-18) ──────────
	// Build-time env: VITE_AGENT_IS_DEFAULT=true makes the Manifest Agent the
	// default experience (wizard behind ?wizard=1); unset/false keeps the
	// classic wizard as default (agent behind ?ai=1). Staging builds with
	// true — the agent is LIVE there; production omits the var until go-live.
	const AGENT_IS_DEFAULT = import.meta.env.VITE_AGENT_IS_DEFAULT === 'true';
	// ?agent=1 deep-links the open Manifest Agent chat (survives refresh); the
	// panel itself sets it, so it also implies the agent experience is enabled.
	const [searchParams, setSearchParams] = useSearchParams();
	const agentEnabled = AGENT_IS_DEFAULT || searchParams.get('ai') === '1' || searchParams.get('agent') === '1';
	const legacyWizardEnabled = !AGENT_IS_DEFAULT || searchParams.get('wizard') === '1';
	const [agentOpen, setAgentOpen] = useState(searchParams.get('agent') === '1');
	const openAgent = useCallback(() => {
		setAgentOpen(true);
		const sp = new URLSearchParams(searchParams);
		sp.set('agent', '1');
		setSearchParams(sp, { replace: true });
	}, [searchParams, setSearchParams]);
	const [attendeeModalOpen, setAttendeeModalOpen] = useState(false);
	const [viewingFile, setViewingFile] = useState<FileViewerFile | null>(null);
	const [fileLoading, setFileLoading] = useState<string | null>(null);

	// View mode
	const [viewMode, setViewMode] = useState<ViewMode>('simple');

	// Filters
	const [filtersExpanded, setFiltersExpanded] = useState(false);
	const [originAirportFilter, setOriginAirportFilter] = useState<string>('__all__');
	const [arrivalAirportFilter, setArrivalAirportFilter] = useState<string>('__all__');
	const [searchQuery, setSearchQuery] = useState('');
	const [flightNumberFilter, setFlightNumberFilter] = useState('');
	const [outboundArrivalDateFilter, setOutboundArrivalDateFilter] = useState('');
	const [returnDepartureDateFilter, setReturnDepartureDateFilter] = useState('');
	const [timeFilter, setTimeFilter] = useState('');
	const [timeWindowMinutes, setTimeWindowMinutes] = useState<number>(30);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

	// Sorting
	const [sortKey, setSortKey] = useState<SortKey>('passenger_name');
	const [sortDir, setSortDir] = useState<SortDir>('asc');

	// Inline editing
	const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
	const [editValue, setEditValue] = useState('');
	const [saving, setSaving] = useState(false);

	// Bulk selection
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const hasSelection = selectedIds.size > 0;

	const handleSelectRow = useCallback((recordId: string) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(recordId)) next.delete(recordId); else next.add(recordId);
			return next;
		});
	}, []);

	const handleSelectPassengerGroup = useCallback((group: PassengerGroup) => {
		const ids = group.bookings.map(b => b.recordId);
		setSelectedIds(prev => {
			const next = new Set(prev);
			const allSelected = ids.every(id => next.has(id));
			if (allSelected) { ids.forEach(id => next.delete(id)); }
			else { ids.forEach(id => next.add(id)); }
			return next;
		});
	}, []);

	const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

	// Delete confirmation
	const [deleteConfirm, setDeleteConfirm] = useState<{
		type: 'passenger' | 'booking' | 'leg' | 'bulk';
		label: string;
		recordIds: string[];
		legInfo?: { recordId: string; allFlightsIndex: number };
	} | null>(null);
	const [deleting, setDeleting] = useState(false);

	// Add leg
	const [addingLeg, setAddingLeg] = useState<{ recordId: string } | null>(null);
	const [newLeg, setNewLeg] = useState({ departure_airport: '', arrival_airport: '', flight_number: '', date: '', departure_time: '', arrival_time: '', direction: 'outbound' as 'outbound' | 'return' });

	const startAddLeg = (recordId: string) => {
		setAddingLeg({ recordId });
		setNewLeg({ departure_airport: '', arrival_airport: '', flight_number: '', date: '', departure_time: '', arrival_time: '', direction: 'outbound' });
	};
	const cancelAddLeg = () => setAddingLeg(null);

	const saveNewLeg = async () => {
		if (!addingLeg || !eventId) return;
		const { recordId } = addingLeg;
		const record = records.find(r => r.id === recordId);
		if (!record) return;
		setSaving(true);
		try {
			// Build the new leg entry
			const entry = {
				departure_airport: newLeg.departure_airport || '',
				arrival_airport: newLeg.arrival_airport || '',
				flight_number: newLeg.flight_number || null,
				date: newLeg.date || '',
				departure_time: newLeg.departure_time || null,
				arrival_time: newLeg.arrival_time || null,
				direction: newLeg.direction,
			};
			// Append to existing all_flights (or build from primary fields if empty)
			let allFlights = record.all_flights ? [...record.all_flights] : [];
			if (allFlights.length === 0) {
				// Bootstrap from primary fields
				if (record.outbound_departure_airport || record.outbound_departure_date) {
					allFlights.push({
						departure_airport: record.outbound_departure_airport || '',
						arrival_airport: record.outbound_arrival_airport || '',
						flight_number: record.outbound_flight_number || null,
						date: record.outbound_departure_date?.slice(0, 10) || '',
						departure_time: record.outbound_departure_time || null,
						arrival_time: record.outbound_arrival_time || null,
						direction: 'outbound' as const,
					});
				}
				if (record.return_departure_airport || record.return_flight_date) {
					allFlights.push({
						departure_airport: record.return_departure_airport || '',
						arrival_airport: record.return_arrival_airport || '',
						flight_number: record.return_flight_number || null,
						date: record.return_flight_date?.slice(0, 10) || '',
						departure_time: record.return_departure_time || null,
						arrival_time: record.return_arrival_time || null,
						direction: 'return' as const,
					});
				}
			}
			allFlights.push(entry);
			// Sort by date then departure_time
			allFlights.sort((a, b) => {
				const da = (a.date || '').slice(0, 10);
				const db = (b.date || '').slice(0, 10);
				if (da !== db) return da.localeCompare(db);
				return (a.departure_time || '').localeCompare(b.departure_time || '');
			});

			const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/records/${recordId}`, {
				method: 'PATCH', headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ all_flights: allFlights }),
			});
			if (!res.ok) throw new Error('Failed to add leg');
			setData(prev => prev ? {
				...prev,
				records: prev.records.map(r => r.id === recordId ? { ...r, all_flights: allFlights } : r),
			} : prev);
			notifySuccess('Flight leg added');
			setAddingLeg(null);
		} catch (e: any) { notifyError(e?.message || 'Failed to add leg'); }
		finally { setSaving(false); }
	};

	const loadData = useCallback(async () => {
		if (!eventId) return;
		setLoading(true);
		try {
			const [eventData, manifestData] = await Promise.all([
				eventsService.get(eventId),
				manifestService.getByEvent(eventId),
			]);
			setEvent(eventData);
			setData(manifestData);
		} catch (e: any) {
			notifyError(e?.message || 'Failed to load data');
		} finally {
			setLoading(false);
		}
	}, [eventId]);

	useEffect(() => { loadData(); }, [loadData]);

	const handleViewSource = async (fileId: string, filename: string) => {
		setFileLoading(fileId);
		try {
			const { url } = await manifestService.getFileViewUrl(fileId);
			setViewingFile({ url, name: filename, mimeType: 'application/pdf' });
		} catch (e: any) {
			notifyError(e?.message || 'Failed to load file');
		} finally {
			setFileLoading(null);
		}
	};

	const handleWizardComplete = () => {
		setWizardOpen(false);
		loadData();
		notifySuccess('Manifest processed successfully');
	};

	const handleSort = (key: SortKey) => {
		if (sortKey === key) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
		else { setSortKey(key); setSortDir('asc'); }
	};

	const startEdit = (recordId: string, field: string, currentValue: string, legSource?: EditingCell['legSource'], allFlightsIndex?: number) => {
		setEditingCell({ recordId, field, legSource, allFlightsIndex });
		setEditValue(currentValue || '');
	};

	const cancelEdit = () => { setEditingCell(null); setEditValue(''); };

	const saveEdit = async () => {
		if (!editingCell || !eventId) return;
		setSaving(true);
		try {
			const record = records.find(r => r.id === editingCell.recordId);
			if (!record) throw new Error('Record not found');

			let updates: Record<string, any> = {};

			if (editingCell.legSource === 'all_flights' && editingCell.allFlightsIndex !== undefined) {
				// Update a specific leg in the all_flights array
				const allFlights = [...(record.all_flights || [])];
				const idx = editingCell.allFlightsIndex;
				if (allFlights[idx]) {
					const fieldMap: Record<string, string> = {
						departure_airport: 'departure_airport',
						arrival_airport: 'arrival_airport',
						flight_number: 'flight_number',
						date: 'date',
						departure_time: 'departure_time',
						arrival_time: 'arrival_time',
					};
					const legField = fieldMap[editingCell.field];
					if (legField) {
						allFlights[idx] = { ...allFlights[idx], [legField]: editValue || null };
						updates = { all_flights: allFlights };
					}
				}
			} else if (editingCell.legSource === 'primary_outbound') {
				const fieldMap: Record<string, string> = {
					departure_airport: 'outbound_departure_airport',
					arrival_airport: 'outbound_arrival_airport',
					flight_number: 'outbound_flight_number',
					date: 'outbound_departure_date',
					departure_time: 'outbound_departure_time',
					arrival_time: 'outbound_arrival_time',
				};
				const dbField = fieldMap[editingCell.field];
				if (dbField) updates[dbField] = editValue || null;
			} else if (editingCell.legSource === 'primary_return') {
				const fieldMap: Record<string, string> = {
					departure_airport: 'return_departure_airport',
					arrival_airport: 'return_arrival_airport',
					flight_number: 'return_flight_number',
					date: 'return_flight_date',
					departure_time: 'return_departure_time',
					arrival_time: 'return_arrival_time',
				};
				const dbField = fieldMap[editingCell.field];
				if (dbField) updates[dbField] = editValue || null;
			} else if (editingCell.field === 'passenger_name_combo') {
				// Special: editValue is "first||last"
				const [first, last] = editValue.split('||');
				updates.passenger_first_name = first?.trim() || null;
				updates.passenger_last_name = last?.trim() || null;
				updates.passenger_name = [first?.trim(), last?.trim()].filter(Boolean).join(' ') || null;
			} else {
				// Direct record field (primary_pnr, etc.)
				updates[editingCell.field] = editValue || null;
			}

			// Try event-level endpoint first, fall back to manifest-level
			const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/records/${editingCell.recordId}`, {
				method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
			});
			if (!res.ok) {
				await manifestService.updateRecord(record.manifest_id, editingCell.recordId, updates);
			}

			notifySuccess('Record updated');
			setData(prev => prev ? {
				...prev,
				records: prev.records.map(r => r.id === editingCell!.recordId ? { ...r, ...updates } : r),
			} : prev);
			cancelEdit();
		} catch (e: any) { notifyError(e?.message || 'Failed to update'); }
		finally { setSaving(false); }
	};

	const handleDeleteConfirm = async () => {
		if (!deleteConfirm || !eventId) return;
		setDeleting(true);
		try {
			if (deleteConfirm.type === 'leg' && deleteConfirm.legInfo) {
				// Remove a specific leg from all_flights array
				const { recordId, allFlightsIndex } = deleteConfirm.legInfo;
				const record = records.find(r => r.id === recordId);
				if (!record) throw new Error('Record not found');
				const allFlights = [...(record.all_flights || [])];
				allFlights.splice(allFlightsIndex, 1);
				const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/records/${recordId}`, {
					method: 'PATCH', headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ all_flights: allFlights }),
				});
				if (!res.ok) throw new Error('Failed to remove leg');
				setData(prev => prev ? {
					...prev,
					records: prev.records.map(r => r.id === recordId ? { ...r, all_flights: allFlights } : r),
				} : prev);
				notifySuccess('Flight leg removed');
			} else {
				// Delete whole record(s) — works for both passenger and booking
				await manifestService.bulkDeleteRecords(eventId, deleteConfirm.recordIds);
				setData(prev => prev ? {
					...prev,
					records: prev.records.filter(r => !deleteConfirm.recordIds.includes(r.id)),
				} : prev);
				const msg = deleteConfirm.type === 'bulk' ? `${deleteConfirm.recordIds.length} record(s) deleted`
					: deleteConfirm.type === 'passenger' ? 'Passenger removed' : 'Booking removed';
				notifySuccess(msg);
				if (deleteConfirm.type === 'bulk') clearSelection();
			}
		} catch (e: any) { notifyError(e?.message || 'Failed to delete'); }
		finally { setDeleting(false); setDeleteConfirm(null); }
	};

	const requestDeletePassenger = (group: PassengerGroup) => {
		const ids = group.bookings.map(b => b.recordId);
		setDeleteConfirm({ type: 'passenger', label: group.name || 'this passenger', recordIds: ids });
	};

	const requestDeleteBooking = (booking: BookingGroup) => {
		setDeleteConfirm({ type: 'booking', label: `booking ${booking.pnr.startsWith('record-') ? '' : booking.pnr}`.trim() || 'this booking', recordIds: [booking.recordId] });
	};

	const requestDeleteLeg = (booking: BookingGroup, leg: FlightLeg) => {
		if (leg.source !== 'all_flights' || leg.allFlightsIndex === undefined) {
			// For primary outbound/return legs, delete the whole record
			setDeleteConfirm({ type: 'booking', label: `this booking record`, recordIds: [booking.recordId] });
			return;
		}
		const route = [leg.departureAirport, leg.arrivalAirport].filter(Boolean).join(' → ') || 'this leg';
		setDeleteConfirm({ type: 'leg', label: route, recordIds: [booking.recordId], legInfo: { recordId: booking.recordId, allFlightsIndex: leg.allFlightsIndex } });
	};

	const requestDeleteRecord = (record: RecordWithSourceDTO) => {
		const name = record.passenger_name || 'this record';
		setDeleteConfirm({ type: 'passenger', label: name, recordIds: [record.id] });
	};

	const records = data?.records || [];
	const manifests = data?.manifests || [];
	const uniqueOriginAirports = useMemo(() => {
		const airports = [...new Set(records.map(r => r.outbound_departure_airport).filter(Boolean))] as string[];
		return airports.sort();
	}, [records]);

	const uniqueArrivalAirports = useMemo(() => {
		const airports = [...new Set(records.map(r => r.outbound_arrival_airport).filter(Boolean))] as string[];
		return airports.sort();
	}, [records]);

	// Shared record-level filter
	const applyRecordFilters = useCallback((list: RecordWithSourceDTO[]) => {
		let filtered = [...list];
		if (originAirportFilter !== '__all__') {
			filtered = filtered.filter(r => r.outbound_departure_airport === originAirportFilter);
		}
		if (arrivalAirportFilter !== '__all__') {
			filtered = filtered.filter(r => r.outbound_arrival_airport === arrivalAirportFilter);
		}
		if (searchQuery.trim()) {
			filtered = filtered.filter(r => recordMatchesSearch(r, searchQuery.trim()));
		}
		if (flightNumberFilter.trim()) {
			const fn = flightNumberFilter.trim().toLowerCase();
			filtered = filtered.filter(r =>
				(r.outbound_flight_number || '').toLowerCase().includes(fn) ||
				(r.return_flight_number || '').toLowerCase().includes(fn) ||
				(r.all_flights || []).some(f => (f.flight_number || '').toLowerCase().includes(fn))
			);
		}
		if (outboundArrivalDateFilter) {
			filtered = filtered.filter(r => normDate(r.outbound_arrival_date) === outboundArrivalDateFilter);
		}
		if (returnDepartureDateFilter) {
			filtered = filtered.filter(r => normDate(r.return_flight_date) === returnDepartureDateFilter);
		}
		if (timeFilter) {
			filtered = filtered.filter(r =>
				timeInWindow(r.outbound_arrival_time, timeFilter, timeWindowMinutes) ||
				timeInWindow(r.return_departure_time, timeFilter, timeWindowMinutes)
			);
		}
		return filtered;
	}, [originAirportFilter, arrivalAirportFilter, statusFilter, searchQuery, flightNumberFilter, outboundArrivalDateFilter, returnDepartureDateFilter, timeFilter, timeWindowMinutes]);

	// Group and filter
	const passengerGroups = useMemo(() => {
		const filteredRecords = applyRecordFilters(records);
		let groups = groupRecordsByPassenger(filteredRecords);

		// Sort
		groups.sort((a, b) => {
			const va = getPassengerSortValue(a, sortKey);
			const vb = getPassengerSortValue(b, sortKey);
			return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
		});

		return groups;
	}, [records, applyRecordFilters, sortKey, sortDir]);

	// Filtered + sorted records for simple view
	const simpleRecords = useMemo(() => {
		const filtered = applyRecordFilters(records);
		filtered.sort((a, b) => {
			let va = '', vb = '';
			switch (sortKey) {
				case 'passenger_name': va = (a.passenger_name || '').toLowerCase(); vb = (b.passenger_name || '').toLowerCase(); break;
				case 'primary_pnr': va = (a.primary_pnr || '').toLowerCase(); vb = (b.primary_pnr || '').toLowerCase(); break;
				case 'outbound_departure_date': va = a.outbound_departure_date || ''; vb = b.outbound_departure_date || ''; break;
			}
			return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
		});
		return filtered;
	}, [records, applyRecordFilters, sortKey, sortDir]);

	// Bulk select all (depends on simpleRecords/passengerGroups)
	const handleSelectAll = useCallback(() => {
		if (viewMode === 'simple') {
			const ids = simpleRecords.map(r => r.id);
			setSelectedIds(prev => {
				const allSelected = ids.length > 0 && ids.every(id => prev.has(id));
				return allSelected ? new Set() : new Set(ids);
			});
		} else {
			const ids = passengerGroups.flatMap(g => g.bookings.map(b => b.recordId));
			setSelectedIds(prev => {
				const allSelected = ids.length > 0 && ids.every(id => prev.has(id));
				return allSelected ? new Set() : new Set(ids);
			});
		}
	}, [viewMode, simpleRecords, passengerGroups]);

	const handleBulkDelete = useCallback(() => {
		if (selectedIds.size === 0) return;
		setDeleteConfirm({
			type: 'bulk',
			label: `${selectedIds.size} record${selectedIds.size !== 1 ? 's' : ''}`,
			recordIds: Array.from(selectedIds),
		});
	}, [selectedIds]);

	// Collect all IATA codes from records for tooltip lookup
	const allIataCodes = useMemo(() => {
		const codes: string[] = [];
		for (const r of records) {
			if (r.outbound_departure_airport) codes.push(r.outbound_departure_airport);
			if (r.outbound_arrival_airport) codes.push(r.outbound_arrival_airport);
			if (r.return_departure_airport) codes.push(r.return_departure_airport);
			if (r.return_arrival_airport) codes.push(r.return_arrival_airport);
			const flights = (r as any).all_flights;
			if (Array.isArray(flights)) {
				for (const f of flights) {
					if (f.departure_airport) codes.push(f.departure_airport);
					if (f.arrival_airport) codes.push(f.arrival_airport);
				}
			}
		}
		return [...new Set(codes)];
	}, [records]);
	const airportNames = useAirportNames(allIataCodes, manifestService);

	const totalRecordCount = records.length;
	const totalFlightCount = records.reduce((sum, r) => sum + ((r as any).all_flights?.length || 0), 0);
	const displayedRecordCount = passengerGroups.reduce((sum, g) => sum + g.bookings.length, 0);
	const matchedCount = records.filter(r => r.matched_attendee_id).length;
	const hasActiveFilters = originAirportFilter !== '__all__' || arrivalAirportFilter !== '__all__' || searchQuery.trim().length > 0 || flightNumberFilter.trim().length > 0 || outboundArrivalDateFilter.length > 0 || returnDepartureDateFilter.length > 0 || timeFilter.length > 0 || statusFilter !== 'all';
	const activeFilterCount = [
		originAirportFilter !== '__all__',
		arrivalAirportFilter !== '__all__',
		flightNumberFilter.trim().length > 0,
		outboundArrivalDateFilter.length > 0,
		returnDepartureDateFilter.length > 0,
		timeFilter.length > 0,
		statusFilter !== 'all',
	].filter(Boolean).length;

	const clearAllFilters = () => {
		setOriginAirportFilter('__all__'); setArrivalAirportFilter('__all__');
		setSearchQuery(''); setFlightNumberFilter('');
		setOutboundArrivalDateFilter(''); setReturnDepartureDateFilter('');
		setTimeFilter(''); setStatusFilter('all');
	};

	// ── Swoogo-format date helper ──
	// Converts various date formats (YYYY-MM-DD, DD/MM/YYYY, etc.) to "22-Sep-2026".
	// Parses the Y-M-D parts directly — `new Date('YYYY-MM-DD')` is UTC-midnight, so
	// viewers in UTC-negative timezones saw the PREVIOUS day on export (Bug 2).
	const formatSwoogoDate = (dateStr: string | null | undefined): string => {
		if (!dateStr) return '';
		const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
		if (m) return `${m[3]}-${months[Number(m[2]) - 1]}-${m[1]}`;
		const dt = new Date(dateStr);
		if (isNaN(dt.getTime())) return dateStr; // fallback to raw string if unparseable
		return `${String(dt.getDate()).padStart(2, '0')}-${months[dt.getMonth()]}-${dt.getFullYear()}`;
	};

	// ── Build Swoogo export rows ──
	const buildSwoogoRows = async (mode: 'all' | 'with-flights') => {
		// Fetch all attendees for this event
		const attendeesService = new (await import('@/services/AttendeesService')).AttendeesService();
		const PAGE_SIZE = 100;
		const firstPage = await attendeesService.listByEvent(eventId!, 1, PAGE_SIZE);
		let allAttendees = [...firstPage.items];
		if (firstPage.pagination.totalPages > 1) {
			const remaining = await Promise.all(
				Array.from({ length: firstPage.pagination.totalPages - 1 }, (_, i) =>
					attendeesService.listByEvent(eventId!, i + 2, PAGE_SIZE)
				)
			);
			for (const page of remaining) allAttendees = allAttendees.concat(page.items);
		}

		// Build lookup from currently visible records (simple view)
		// This ensures export matches exactly what the user sees in the table
		const visibleRecords = applyRecordFilters(records);
		const recordByAttendee = new Map<string, RecordWithSourceDTO>();
		for (const r of visibleRecords) {
			if (!r.matched_attendee_id) continue;
			const existing = recordByAttendee.get(r.matched_attendee_id);
			if (!existing || (r.is_approved && !existing.is_approved)) {
				recordByAttendee.set(r.matched_attendee_id, r);
			}
		}

		// Bug 6: attendees arrived in arbitrary API order, so exported rows shuffled
		// between exports. Sort by last name, then first name (case-insensitive).
		const byName = (a: any, b: any) => {
			const la = [a.last_name || '', a.first_name || ''].join(' ').trim().toLowerCase();
			const lb = [b.last_name || '', b.first_name || ''].join(' ').trim().toLowerCase();
			return la.localeCompare(lb);
		};
		const attendeesToExport = (mode === 'with-flights'
			? allAttendees.filter(a => recordByAttendee.has(a.id))
			: allAttendees
		).slice().sort(byName);

		return attendeesToExport.map(a => {
			const r = recordByAttendee.get(a.id);
			const hasFlights = !!r;
			return [
				(a as any).external_ref || '',
				a.first_name || '',
				a.last_name || '',
				hasFlights ? 'Y' : 'N',
				r?.primary_pnr || '',
				r?.outbound_departure_airport || '',
				r?.outbound_arrival_airport || '',
				formatSwoogoDate(r?.outbound_departure_date),
				formatSwoogoDate(r?.outbound_arrival_date),
				r?.outbound_flight_number || '',
				r?.outbound_departure_time || '',
				r?.outbound_arrival_time || '',
				r?.return_departure_airport || '',
				r?.return_arrival_airport || '',
				formatSwoogoDate(r?.return_flight_date),
				r?.return_flight_number || '',
				r?.return_departure_time || '',
				'', // Notes
			];
		});
	};

	const SWOOGO_HEADERS = [
		'System ID (DO NOT CHANGE)', 'First Name', 'Last Name',
		'Have your flights been booked?', 'PNR',
		'Outbound Departure Airport Code', 'Outbound  Arrival Airport Code',
		'Outbound Departure Date', 'Outbound Arrival Date',
		'Outbound Flight Number', 'Outbound Departure Time', 'Outbound Arrival Time',
		'Return Departure Airport Code', 'Return Arrival Airport Code',
		'Return Departure Date', 'Return Flight Number', 'Return Departure Time',
		'Notes',
	];

	// CSV Export (Swoogo format)
	const exportCSV = async (mode: 'all' | 'with-flights' = 'all') => {
		try {
			const rows = await buildSwoogoRows(mode);
			const csvContent = [SWOOGO_HEADERS, ...rows]
				.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
				.join('\n');
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `flight-manifests-${event?.name || eventId}-${new Date().toISOString().slice(0, 10)}.csv`;
			link.click();
			URL.revokeObjectURL(url);
		} catch (e: any) {
			notifyError(e?.message || 'Export failed');
		}
	};

	// Excel Export (Swoogo format)
	const exportExcel = async (mode: 'all' | 'with-flights' = 'all') => {
		try {
			const rows = await buildSwoogoRows(mode);
			const tsvContent = [SWOOGO_HEADERS, ...rows].map(row => row.join('\t')).join('\n');
			const blob = new Blob([tsvContent], { type: 'application/vnd.ms-excel' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `flight-manifests-${event?.name || eventId}-${new Date().toISOString().slice(0, 10)}.xls`;
			link.click();
			URL.revokeObjectURL(url);
		} catch (e: any) {
			notifyError(e?.message || 'Export failed');
		}
	};

	// Full-screen early returns (after all hooks)
	if (agentOpen && agentEnabled && eventId) {
		return (
			<ManifestAgentPanel
				eventId={eventId}
				onClose={() => {
					setAgentOpen(false);
					const sp = new URLSearchParams(searchParams);
					sp.delete('agent');
					setSearchParams(sp, { replace: true });
					void loadData();
				}}
			/>
		);
	}
	if (wizardOpen && eventId) {
		return <CreateManifestWizard preSelectedEventId={eventId} onComplete={handleWizardComplete} onCancel={() => setWizardOpen(false)} />;
	}

	return (
		<PageContainer>
			<PageHeader
				title={event ? `${event.name} \u2014 Flight Manifests` : 'Flight Manifests'}
				description={loading ? 'Loading...' : hasActiveFilters ? `${passengerGroups.length} passengers (filtered from ${totalRecordCount} manifests)` : `${totalRecordCount} manifests, ${totalFlightCount} flights across ${manifests.length} batch(es)`}
			>
				<div className="flex items-center gap-2">
					{AGENT_IS_DEFAULT ? (
						<>
							{legacyWizardEnabled && (
								<Button variant="outline" onClick={() => setWizardOpen(true)} className="gap-2">
									<Plus className="w-4 h-4" /> Wizard (legacy)
								</Button>
							)}
							<Button onClick={openAgent} className="gap-2">
								<Bot className="w-4 h-4" /> Manifest Agent
							</Button>
						</>
					) : (
						<>
							{agentEnabled && (
								<Button variant="outline" onClick={openAgent} className="gap-2">
									<Bot className="w-4 h-4" /> Manifest Agent
								</Button>
							)}
							<Button onClick={() => setWizardOpen(true)} className="gap-2">
								<Plus className="w-4 h-4" /> Add New Flight PDFs
							</Button>
						</>
					)}
				</div>
			</PageHeader>

			{/* Action bar */}
			{!loading && (
				<div className="flex gap-3 mb-6">
					<ExportDropdown onExportCSV={exportCSV} onExportExcel={exportExcel} disabled={records.length === 0} />
					<Button variant="outline" onClick={() => setAttendeeModalOpen(true)} className="gap-2">
						<Users className="w-4 h-4" /> Attendee Management
					</Button>
				</div>
			)}

			{loading ? (
				<div className="flex justify-center py-12"><Spinner /></div>
			) : (
				<>
					{/* Stats */}
					<div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
						<StatCard className="!p-3" label="Total Manifests" value={totalRecordCount} icon={<FileText className="w-5 h-5" />} />
						<StatCard className="!p-3" label="Total Flights" value={totalFlightCount} icon={<FileText className="w-5 h-5" />} />
						<StatCard className="!p-3" label="Passengers" value={new Set(records.map(r => r.passenger_name).filter(Boolean)).size} icon={<User className="w-5 h-5" />} />
						<StatCard className="!p-3" label="Attendees Matched" value={matchedCount} icon={<CheckCircle className="w-5 h-5" />} />
						<StatCard className="!p-3" label="Date Range" value={getDateRange(records)} icon={<RefreshCw className="w-5 h-5" />} />
					</div>

					{/* Filter/Search Bar */}
					{records.length > 0 && (
						<div className="mb-4">
							{/* Always-visible: search + filter toggle */}
							<div className="flex items-center gap-3">
								<div className="flex-1">
									<Input placeholder="Search by name, PNR, email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} leftIcon={<Search className="w-4 h-4" />} />
								</div>
								<Button
									variant={filtersExpanded ? 'secondary' : 'outline'}
									onClick={() => setFiltersExpanded(v => !v)}
									className="gap-1.5 whitespace-nowrap"
								>
									<Settings2 className="w-4 h-4" />
									Filters
									{activeFilterCount > 0 && (
										<span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-medium">{activeFilterCount}</span>
									)}
								</Button>
								{hasActiveFilters && (
									<Button variant="ghost" size="sm" onClick={clearAllFilters} className="gap-1 whitespace-nowrap text-gray-500">
										<X className="w-3.5 h-3.5" /> Clear
									</Button>
								)}
							</div>

							{/* Collapsible advanced filters */}
							{filtersExpanded && (
								<div className="mt-3 bg-white rounded-lg border border-gray-200 p-4 space-y-3">
									<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
										<Input label="Flight Number" placeholder="e.g. DL2487" value={flightNumberFilter} onChange={(e) => setFlightNumberFilter(e.target.value)} />
										<Select label="Departure Airport" placeholder="All" value={originAirportFilter}
											onValueChange={(v) => setOriginAirportFilter(v)}
											options={[{ value: '__all__', label: 'All airports' }, ...uniqueOriginAirports.map(a => ({ value: a, label: a }))]}
										/>
										<Select label="Arrival Airport" placeholder="All" value={arrivalAirportFilter}
											onValueChange={(v) => setArrivalAirportFilter(v)}
											options={[{ value: '__all__', label: 'All airports' }, ...uniqueArrivalAirports.map(a => ({ value: a, label: a }))]}
										/>
										<div>
											<label className="block text-xs font-medium text-gray-700 mb-1">Time Window</label>
											<Select value={String(timeWindowMinutes)}
												onValueChange={(v) => setTimeWindowMinutes(Number(v))}
												options={[
													{ value: '30', label: '\u00B130 min' },
													{ value: '60', label: '\u00B11 hour' },
													{ value: '120', label: '\u00B12 hours' },
												]}
											/>
										</div>
									</div>
									<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
										<div>
											<label className="block text-xs font-medium text-gray-700 mb-1">Outbound Arrival Date</label>
											<input type="date" value={outboundArrivalDateFilter} onChange={(e) => setOutboundArrivalDateFilter(e.target.value)}
												className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
										</div>
										<div>
											<label className="block text-xs font-medium text-gray-700 mb-1">Return Departure Date</label>
											<input type="date" value={returnDepartureDateFilter} onChange={(e) => setReturnDepartureDateFilter(e.target.value)}
												className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
										</div>
										<div>
											<label className="block text-xs font-medium text-gray-700 mb-1">Time (Arr / Ret Dep)</label>
											<input type="time" value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}
												className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
										</div>
									</div>
								</div>
							)}
						</div>
					)}

					{/* View mode toggle */}
					{records.length > 0 && (
						<div className="flex items-center justify-end mb-3">
							<div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
								<button
									onClick={() => setViewMode('simple')}
									className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
										viewMode === 'simple' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
									}`}
								>
									<List className="w-3.5 h-3.5" /> Simple
								</button>
								<button
									onClick={() => setViewMode('advanced')}
									className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
										viewMode === 'advanced' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
									}`}
								>
									<Settings2 className="w-3.5 h-3.5" /> Advanced
								</button>
							</div>
						</div>
					)}

					{/* Bulk action bar */}
					{hasSelection && (
						<div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
							<div className="flex items-center justify-between">
								<span className="text-sm font-medium text-primary-900">
									<span className="font-bold">{selectedIds.size}</span> record{selectedIds.size !== 1 ? 's' : ''} selected
								</span>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										onClick={handleBulkDelete}
										className="text-red-600 hover:text-red-700 hover:border-red-300"
									>
										<Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete Selected
									</Button>
									<Button variant="ghost" size="sm" onClick={clearSelection}>
										Clear Selection
									</Button>
								</div>
							</div>
						</div>
					)}

					{/* Records table */}
					{records.length === 0 ? (
						<Card className="text-center py-12">
							<CardContent>
								<div className="flex flex-col items-center space-y-4">
									<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"><FileText className="w-8 h-8 text-gray-400" /></div>
									<div className="space-y-2"><h3 className="text-lg font-semibold">No Records Yet</h3><p className="text-gray-600 max-w-md">Upload and process flight manifest PDFs to see extracted passenger data here.</p></div>
									{AGENT_IS_DEFAULT ? (
										<Button onClick={openAgent} className="gap-2"><Bot className="w-4 h-4" /> Open the Manifest Agent</Button>
									) : (
										<Button onClick={() => setWizardOpen(true)} className="gap-2"><Plus className="w-4 h-4" /> Add New Flight PDFs</Button>
									)}
								</div>
							</CardContent>
						</Card>
					) : (viewMode === 'simple' ? simpleRecords.length === 0 : passengerGroups.length === 0) ? (
						<Card className="text-center py-12">
							<CardContent>
								<div className="flex flex-col items-center space-y-4">
									<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"><Search className="w-8 h-8 text-gray-400" /></div>
									<div className="space-y-2"><h3 className="text-lg font-semibold">No matching records</h3><p className="text-gray-600 max-w-md">No records match the current filters.</p></div>
									<Button variant="outline" onClick={clearAllFilters}>Clear filters</Button>
								</div>
							</CardContent>
						</Card>
					) : viewMode === 'simple' ? (
						<div className="border rounded-lg overflow-hidden bg-white">
							<div className="overflow-x-auto">
								<table className="w-full text-sm min-w-[1400px]">
									<thead>
										<tr className="border-b bg-gray-50">
											<th className="p-2 w-[40px] text-center">
												<input
													type="checkbox"
													checked={simpleRecords.length > 0 && simpleRecords.every(r => selectedIds.has(r.id))}
													onChange={handleSelectAll}
													className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
													aria-label="Select all records"
												/>
											</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500 w-[36px]"></th>
											<SortableHeader label="Passenger" sortKey="passenger_name" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
											<SortableHeader label="PNR" sortKey="primary_pnr" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Out Dep</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Out Arr</th>
											<SortableHeader label="Out Date" sortKey="outbound_departure_date" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Out Arr Date</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Out Flight</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Out Dep Time</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Out Arr Time</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Ret Dep</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Ret Arr</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Ret Date</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Ret Flight</th>
											<th className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500">Ret Dep Time</th>
											<th className="w-[32px]"></th>
										</tr>
									</thead>
									<tbody>
										{simpleRecords.map((record) => (
											<SimpleRecordRow
												key={record.id}
												record={record}
												fileLoading={fileLoading}
												onViewSource={handleViewSource}
												onDelete={requestDeleteRecord}
												selected={selectedIds.has(record.id)}
												onSelect={() => handleSelectRow(record.id)}
											/>
										))}
									</tbody>
								</table>
							</div>
						</div>
					) : (
						/* ── Advanced view: grouped by passenger with inline editing ── */
						<div className="border rounded-lg overflow-hidden bg-white">
							<div className="overflow-x-auto">
								<table className="w-full text-sm min-w-[1050px] table-fixed">
									<colgroup>
										<col style={{ width: '40px' }} />
										<col style={{ width: '48px' }} />
										<col style={{ width: '18%' }} />
										<col style={{ width: '13%' }} />
										<col style={{ width: '9%' }} />
										<col style={{ width: '11%' }} />
										<col style={{ width: '8%' }} />
										<col style={{ width: '8%' }} />
										<col style={{ width: '10%' }} />
										<col style={{ width: '12%' }} />
										<col style={{ width: '32px' }} />
									</colgroup>
									<thead>
										<tr className="border-b bg-gray-50">
											<th className="p-2 text-center">
												<input
													type="checkbox"
													checked={passengerGroups.length > 0 && passengerGroups.every(g => g.bookings.every(b => selectedIds.has(b.recordId)))}
													onChange={handleSelectAll}
													className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
													aria-label="Select all records"
												/>
											</th>
											<th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wide text-gray-500">Status</th>
											<SortableHeader label="Passenger" sortKey="passenger_name" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
											<th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wide text-gray-500">Route</th>
											<th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wide text-gray-500">Flight</th>
											<SortableHeader label="Date" sortKey="outbound_departure_date" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
											<th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wide text-gray-500">Depart</th>
											<th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wide text-gray-500">Arrive</th>
											<SortableHeader label="Booking Ref" sortKey="primary_pnr" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
											<th className="text-left p-3 font-semibold text-[11px] uppercase tracking-wide text-gray-500">Source</th>
											<th className="p-1" />
										</tr>
									</thead>
									<tbody>
										{passengerGroups.map((group) => (
											<PassengerGroupRows
												key={group.key}
												group={group}
												editingCell={editingCell}
												editValue={editValue}
												saving={saving}
												fileLoading={fileLoading}
												manifestService={manifestService}
												onStartEdit={startEdit}
												onCancelEdit={cancelEdit}
												onSaveEdit={saveEdit}
												onEditValueChange={setEditValue}
												onViewSource={handleViewSource}
												onDeletePassenger={requestDeletePassenger}
												onDeleteBooking={requestDeleteBooking}
												onDeleteLeg={requestDeleteLeg}
												addingLeg={addingLeg}
												newLeg={newLeg}
												onAddLeg={startAddLeg}
												onCancelAddLeg={cancelAddLeg}
												onSaveNewLeg={saveNewLeg}
												onNewLegChange={setNewLeg}
												airportNames={airportNames}
												selected={group.bookings.every(b => selectedIds.has(b.recordId))}
												onSelect={() => handleSelectPassengerGroup(group)}
											/>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</>
			)}

			<FileViewerModal isOpen={!!viewingFile} onClose={() => setViewingFile(null)} file={viewingFile} />
			<ConfirmDialog
				isOpen={!!deleteConfirm}
				onClose={() => setDeleteConfirm(null)}
				onConfirm={handleDeleteConfirm}
				title={`Delete ${deleteConfirm?.type || 'item'}?`}
				description={`Are you sure you want to delete ${deleteConfirm?.label || 'this item'}? This action cannot be undone.`}
				confirmText="Delete"
				variant="destructive"
				isLoading={deleting}
			/>
			<AttendeeManagementModal
				isOpen={attendeeModalOpen}
				onClose={() => setAttendeeModalOpen(false)}
				eventId={eventId!}
				eventName={event?.name}
				manifestRecords={records}
				onAttendeesChanged={loadData}
			/>
		</PageContainer>
	);
}

// ═══════════════════════════════════════════════════════════════════
// Simple Record Row (read-only, one row per record)
// ═══════════════════════════════════════════════════════════════════

function SimpleRecordRow({ record, fileLoading, onViewSource, onDelete, selected, onSelect }: {
	record: RecordWithSourceDTO;
	fileLoading: string | null;
	onViewSource: (fileId: string, filename: string) => void;
	onDelete: (record: RecordWithSourceDTO) => void;
	selected: boolean;
	onSelect: () => void;
}) {
	const isMissingName = !record.passenger_name;
	const bgTint = '';

	const statusBadge = (() => {
		if (record.is_approved) {
			return (
				<SimpleTooltip content="Confirmed">
					<span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600 cursor-help">{'\u2713'}</span>
				</SimpleTooltip>
			);
		}
		return (
			<SimpleTooltip content="Pending review">
				<span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-50 text-gray-400 cursor-help">{'\u2014'}</span>
			</SimpleTooltip>
		);
	})();

	const cell = "p-2 whitespace-nowrap overflow-hidden text-xs text-gray-700";
	const mono = "font-mono text-xs";

	return (
		<tr className={`group border-b hover:bg-gray-100/50 transition-colors ${bgTint} ${selected ? 'bg-primary-50/40' : ''}`}>
			<td className="p-2 text-center">
				<input
					type="checkbox"
					checked={selected}
					onChange={onSelect}
					className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
					aria-label={`Select ${record.passenger_name || 'record'}`}
				/>
			</td>
			<td className="p-2 text-center">{statusBadge}</td>
			<td className={`${cell} min-w-[140px]`}>
				{isMissingName ? (
					<span className="text-red-600 italic text-xs">{'\u2014'} <em>Pending</em></span>
				) : (
					<span className="text-xs font-semibold">{record.passenger_name}</span>
				)}
			</td>
			<td className={cell}><span className={mono}>{record.primary_pnr || '\u2014'}</span></td>
			<td className={cell}><span className={mono}>{record.outbound_departure_airport || '\u2014'}</span></td>
			<td className={cell}><span className={mono}>{record.outbound_arrival_airport || '\u2014'}</span></td>
			<td className={cell}>{formatDateCompact(record.outbound_departure_date)}</td>
			<td className={cell}>{formatDateCompact(record.outbound_arrival_date)}</td>
			<td className={cell}><span className={mono}>{record.outbound_flight_number || '\u2014'}</span></td>
			<td className={cell}>{formatTime24(record.outbound_departure_time)}</td>
			<td className={cell}>{formatTime24(record.outbound_arrival_time)}</td>
			<td className={cell}><span className={mono}>{record.return_departure_airport || '\u2014'}</span></td>
			<td className={cell}><span className={mono}>{record.return_arrival_airport || '\u2014'}</span></td>
			<td className={cell}>{formatDateCompact(record.return_flight_date)}</td>
			<td className={cell}><span className={mono}>{record.return_flight_number || '\u2014'}</span></td>
			<td className={cell}>{formatTime24(record.return_departure_time)}</td>
			<td className="p-1 text-center">
				<button onClick={() => onDelete(record)} className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-gray-300 hover:text-red-500" title="Delete record">
					<Trash2 className="w-3.5 h-3.5" />
				</button>
			</td>
		</tr>
	);
}

function SortableHeader({ label, sortKey, currentSort, currentDir, onSort }: {
	label: string; sortKey: SortKey; currentSort: SortKey; currentDir: SortDir; onSort: (key: SortKey) => void;
}) {
	const isActive = currentSort === sortKey;
	return (
		<th className="text-left p-3 font-semibold whitespace-nowrap text-[11px] uppercase tracking-wide text-gray-500 cursor-pointer hover:text-gray-800 select-none" onClick={() => onSort(sortKey)}>
			<span className="inline-flex items-center gap-1">
				{label}
				{isActive ? (currentDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <span className="w-3 h-3" />}
			</span>
		</th>
	);
}

// ═══════════════════════════════════════════════════════════════════
// Detect Anomalies Button
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// Export Dropdown
// ═══════════════════════════════════════════════════════════════════

function ExportDropdown({ onExportCSV, onExportExcel, disabled }: {
	onExportCSV: (mode: 'all' | 'with-flights') => void;
	onExportExcel: (mode: 'all' | 'with-flights') => void;
	disabled: boolean;
}) {
	const [open, setOpen] = useState(false);

	const items: { label: string; action: () => void }[] = [
		{ label: 'CSV — All attendees', action: () => onExportCSV('all') },
		{ label: 'CSV — With flights only', action: () => onExportCSV('with-flights') },
		{ label: 'Excel — All attendees', action: () => onExportExcel('all') },
		{ label: 'Excel — With flights only', action: () => onExportExcel('with-flights') },
	];

	return (
		<div className="relative">
			<Button variant="outline" onClick={() => setOpen(!open)} disabled={disabled} className="gap-2">
				<Download className="w-4 h-4" /> Export
			</Button>
			{open && (
				<>
					<div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
					<div className="absolute left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
						{items.map((item, i) => (
							<button
								key={i}
								onClick={() => { item.action(); setOpen(false); }}
								className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
							>
								{item.label}
							</button>
						))}
					</div>
				</>
			)}
		</div>
	);
}
